import type { Article } from '@/data/articles';
import { THEME_REGISTRY } from '@/themes/index';

interface Props {
  article: Article;
  themeId: string;
}

/** Accepts only hex color strings to prevent CSS injection. */
function safeHex(color: string): string {
  return /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : '#D4A843';
}

export function StoryHero({ article, themeId }: Props) {
  const accentColor = safeHex(THEME_REGISTRY[themeId]?.accentColor ?? '#D4A843');
  const coverImage = article.content.hero?.coverImage ?? '';

  return (
    <div
      role="img"
      aria-label={`Cover photo for: ${article.title}`}
      className="relative w-full min-h-[500px] flex flex-col justify-end bg-black"
      style={{
        /* Use svh (small viewport height) for iOS Safari address-bar safety;
           fall back to 100vh in browsers that don't support it. */
        height: '100svh',
        minHeight: 500,
        backgroundImage: coverImage ? `url(${coverImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Gradient overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to top, rgba(0,0,0,0.90) 0%, rgba(0,0,0,0.40) 50%, rgba(0,0,0,0.15) 100%)',
        }}
      />

      {/* Theme pill — top right */}
      <div className="absolute top-4 right-4 z-10">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-white/60 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full border border-white/10">
          {themeId.replace(/-/g, ' ')}
        </span>
      </div>

      {/* Bottom content */}
      <div className="relative z-10 px-6 pb-20 md:px-16 max-w-5xl">
        <span
          className="inline-block text-[11px] font-bold uppercase tracking-widest border px-3 py-1 mb-5"
          style={{ color: accentColor, borderColor: accentColor }}
        >
          {article.category}
        </span>
        <h1 className="text-4xl md:text-6xl font-bold text-white leading-[1.1] mb-5 max-w-3xl">
          {article.title}
        </h1>
        {article.content.hero?.subtitle && (
          <p className="text-lg text-white/70 mb-4 max-w-2xl leading-relaxed hidden md:block">
            {article.content.hero.subtitle}
          </p>
        )}
        <p className="text-sm text-white/50 uppercase tracking-widest">
          By {article.author}&nbsp;&nbsp;·&nbsp;&nbsp;{article.readTime}&nbsp;min read
        </p>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 animate-bounce opacity-50">
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}
